/**
 * An attachment's confidentiality label.
 *
 * **The label is not access control** (PRD §5.10). Everything sits behind one
 * login, so marking a file CONFIDENTIAL changes how it is presented and
 * nothing about who can open it. Two consequences for this component:
 *
 *  - **No padlock, no shield, no key.** Those icons say "protected", and the
 *    plan is explicit that implying protection which does not exist is worse
 *    than not marking the file at all. The badge is a word.
 *  - **`warn`, not `bad`.** A red badge reads as "blocked" or "you should not
 *    be seeing this", which is untrue on both counts — anyone signed in can
 *    read it. Amber says "handle with care", which is exactly what the label
 *    means.
 *
 * INTERNAL renders nothing. It is the default and the overwhelming majority,
 * so a badge on every row would be noise that trains people to ignore the one
 * badge that matters.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.8, PRD §5.10, §9.7
 */
import { useTranslation } from 'react-i18next';

import type { Confidentiality } from '@nexus/contracts';

import { Badge } from '../ui/Badge';

export interface ConfidentialityBadgeProps {
  confidentiality: Confidentiality;
  className?: string;
}

export function ConfidentialityBadge({
  confidentiality,
  className,
}: ConfidentialityBadgeProps): React.JSX.Element | null {
  const { t } = useTranslation();

  if (confidentiality !== 'CONFIDENTIAL') return null;

  return (
    /* `className` spread conditionally: `exactOptionalPropertyTypes` rejects
       an explicit `undefined` for an optional prop. */
    <Badge tone="warn" {...(className === undefined ? {} : { className })}>
      {t('attachment.confidentiality.CONFIDENTIAL')}
    </Badge>
  );
}
